import { ReactNode } from 'react'
import Dialog from '@mui/material/Dialog'
import { useTranslation } from 'react-i18next'

import PdfViewer from 'src/components/shared/PdfViewer'
import DialogActions from 'src/components/shared/ui/DialogActions'
import DialogContent from 'src/components/shared/ui/DialogContent'
import DialogTitle from 'src/components/shared/ui/DialogTitle/DialogTitle'
import { DownloadIcon } from 'src/components/shared/ui/Icons'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { theme } from 'src/providers/ThemeProvider'
import ButtonPrimaryOutlined from '../ui/ButtonPrimaryOutlined'
import useStyles from './useStyles'

type Props = {
  actions?: ReactNode[]
  allowDownload?: boolean
  documentB64?: string | null
  documentUri?: string | null
  filename?: string | null
  loading?: boolean
  isOpened: boolean
  onClose: () => void
  onPdfError?: () => void
}

export default function StatelessPdfDialog({
  actions = [],
  allowDownload = true,
  documentB64 = null,
  documentUri,
  filename,
  loading = false,
  isOpened = false,
  onClose,
  onPdfError
}: Props): JSX.Element {
  const classes = useStyles()
  const { t } = useTranslation()

  function onDownload() {
    if (documentUri) {
      window.open(documentUri, '_blank')
    }
  }

  const document = documentB64 || documentUri || null

  const icons =
    allowDownload && document
      ? [
          <ButtonPrimaryOutlined onClick={onDownload} variant="text">
            <DownloadIcon
              htmlColor={theme.corporate.primaryColor}
              sx={{ fontSize: '22px', marginRight: '4px' }}
            />
            {t('actions.download')}
          </ButtonPrimaryOutlined>
        ]
      : []

  const loader = (
    <div className={classes.loader}>
      <Loader />
    </div>
  )

  return (
    <>
      <Dialog
        classes={{ paper: classes.paper }}
        onClose={onClose}
        open={isOpened}
        fullWidth
        maxWidth="md"
        fullScreen={false}
      >
        <DialogTitle
          id="pdf-dialog"
          // onClose={onClose}
          // icons={icons}
          className={classes.dialogTitle}
        >
          {filename ?? ''}
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <div className={classes.pdfViewer}>
            {loading || !document ? (
              loader
            ) : (
              <PdfViewer document={document} loader={loader} onError={onPdfError} />
            )}
          </div>
        </DialogContent>
        {actions.length > 0 || icons?.length > 0 ? (
          <DialogActions>
            {icons}
            {actions}
            <ButtonPrimaryOutlined onClick={onClose}>{t('actions.close')}</ButtonPrimaryOutlined>
          </DialogActions>
        ) : null}
      </Dialog>
    </>
  )
}
